import { patternAsString } from "../../pattern/serializePattern";
import { IpaType } from "../../phonemes/ipa";
import { compileRuleSet } from "../../rules/compileRuleSet";
import { SegmentStrategy } from "../../rules/intoSegments";
import { LengthRules, PhoneLengths } from "../../rules/lengthRules";
import { Rule } from "../../rules/rule";
import { CompiledRuleSet, getJustRules, PreprocessOptions, Rules2Type, RuleSet, RuleType } from "../../rules/ruleSet";
import { failedRuleToString } from "../../rules/rulesRunner";
import { ApplyRulesResult, runRulesOnIpa } from "../../rules/runRules";
import { isRuleParsingError, ruleParsingErrorToString, ruleToString, stringToRule } from "../../rules/serializeRule";
import { isStressParseError, stressParseErrorToString, stringToStressMatches } from "../../stress/serializeStressRule";
import { StressRule } from "../../stress/stressRule";

const emptyRuleSet: RuleSet = {
  preprocess: ['lowerCase', 'cleanPunctuation'],
  inputSegments: SegmentStrategy.Single,
  outputSegments: SegmentStrategy.PreferIpaPlus,
  inputSegments2: SegmentStrategy.PreferIpaPlus,
  stressRule: { default: 1 },
}

/** handle rules for converting from custom orthography to IPA */
export class ConversionRules {
  private version = 1;  /** incremented whenever the rules change */
  private ruleSet: RuleSet = emptyRuleSet;
  private compiledRules: CompiledRuleSet | null = null;
  private preLowerCase = true;
  private prePunctuation = true;
  private stressDefault: number | undefined = 1;
  private lengthMap: Record<IpaType, number> = {};

  constructor() {
    this.compileRules(this.ruleSet);
  }

  getVersion() { return this.version; }

  /** use current rules to convert custom orthography to IPA */
  convert(ortho: string): IpaType {
    if (this.compiledRules === null) {
      return ortho;
    }
    const result: ApplyRulesResult = runRulesOnIpa(ortho, this.compiledRules);
    return result.ipa;
  }

  getRuleSet(): RuleSet { return this.ruleSet; }
  setRuleSet(ruleSet: RuleSet): true | string[] {
    const result = this.compileRules(ruleSet);
    if (result !== true) {
      return result;
    }
    // update internal cache
    this.preLowerCase = this.ruleSet.preprocess?.includes('lowerCase') === true;
    this.prePunctuation = this.ruleSet.preprocess?.includes('cleanPunctuation') === true;
    this.stressDefault = this.ruleSet.stressRule?.default;
    this.lengthMap = this.ruleSet.phonemeLengths?.map ?? {};
    return result;
  }
  clearRuleSet() {
    this.preLowerCase = true;
    this.prePunctuation = true;
    this.stressDefault = 1;
    this.lengthMap = {};
    this.ruleSet = emptyRuleSet;
    this.compileRules(this.ruleSet);
  }

  /** get rules for adding phoneme length */
  getAddPhonemeLength(): PhoneLengths | undefined {
    return this.compiledRules === null ? undefined : this.compiledRules.addPhonemeLength;
  }

  setPreLowerCase(on: boolean) {
    this.preLowerCase = on;
    this.updatePreprocess();
  }
  getPreLowerCase(): boolean { return this.preLowerCase; }
  setPrePunctuation(on: boolean) {
    this.prePunctuation = on;
    this.updatePreprocess();
  }
  getPrePunctuation(): boolean { return this.prePunctuation; }

  /**
   * take a list of rules (e.g., 'a > o / _#'), an optional second rule list, and optional stress rules and compile then
   * @returns true on success, otherwise a list of errors
   */
  setRulesAndCompile(rulesStr: string, rules2Str?: string, stressStr?: string): true | string[] {
    // 'rules' section
    const parsed = this.parseRules(rulesStr);
    if (typeof parsed[0] === 'string') {
      return parsed as string[];
    }
    let ruleSet = parsed.length === 0 ? this.ruleSet
      : { ...this.ruleSet, rules: parsed as Rule[], rules2: undefined };

    // 'rules2' section
    if (rules2Str && rules2Str !== '') {
      const parsed2 = this.parseMultiRules(rules2Str);
      if (typeof parsed2[0] === 'string') {
        return parsed2 as string[];
      }
      if (parsed2.length !== 0) {
        ruleSet = { ...ruleSet, rules2: parsed2 as Rules2Type };
      }
    }

    // 'stressRule' section
    if (stressStr) {
      const result = stringToStressMatches(stressStr);
      if (isStressParseError(result)) {
        const error = stressParseErrorToString(result);
        const retval = `stress rule ${error}`;
        return [retval];
      }
      const stressRule: StressRule<IpaType> = {
        default: this.stressDefault ?? 1,
        toCheck: result
      }
      ruleSet = { ...ruleSet, stressRule };
    } else if (this.stressDefault !== undefined) {
      ruleSet = { ...ruleSet, stressRule: { default: this.stressDefault } };
    } else {
      ruleSet = { ...ruleSet, stressRule: undefined };
    }

    return this.compileRules(ruleSet);
  }

  getRulesAsString(): string {
    if (this.ruleSet.rules === undefined) {
      return '';
    }
    return this.ruleSet.rules.map(r => ruleToString(r)).join('\n');
  }
  getRules2AsString(): string {
    const rules2 = this.ruleSet.rules2;
    if (rules2 === undefined) {
      return '';
    }
    if (!Array.isArray(rules2[0])) {
      return (rules2 as RuleType[]).map(rule => ruleToString(rule)).join('\n');
    }

    const multirules = rules2 as RuleType[][];
    let str = '';
    let i = 0;
    for (const list of multirules) {
      if (i++ !== 0) {
        str += '\nreset\n';
      }
      const listStr = list.map(rule => ruleToString(rule)).join('\n');
      str += listStr;
    }
    return str;
  }
  getCustomStressAsString(): string {
    const toCheck = this.ruleSet.stressRule?.toCheck;
    if (toCheck === undefined || toCheck.length === 0) {
      return '';
    }
    return toCheck.map(tc => `${tc.which} ${patternAsString(tc.pattern)}`).join('\n');
  }

  /** set the default syllable for adding stress, if any */
  setStressDefault(which?: number) {
    this.stressDefault = which;
    const toCheck = this.ruleSet.stressRule && this.ruleSet.stressRule.toCheck
      ? this.ruleSet.stressRule.toCheck : undefined;
    const stressRule: StressRule<IpaType> | undefined =
      which === undefined && toCheck === undefined ? undefined
        : which === undefined ? { default: 1, toCheck }
          : toCheck === undefined ? { default: which }
            : { default: which, toCheck };
    this.ruleSet = { ...this.ruleSet, stressRule };
  }
  getStressDefault() { return this.stressDefault; }

  /** get the list of vowel lengths */
  getLengthMap() { return this.lengthMap; }
  setLengthMap(lengthMap: Record<string, number>) {
    this.lengthMap = lengthMap;
    const phonemeLengths: LengthRules = { map: this.lengthMap };
    this.ruleSet = { ...this.ruleSet, phonemeLengths };
  }

  /** return info about rules that will be run for the given keystroke */
  getKeyHelp(keystroke: string): string[] {
    const help: string[] = [];

    // if lowercase will change the symbol, add it to list
    if (this.preLowerCase) {
      const lower = keystroke.toLowerCase();
      if (lower !== keystroke) {
        help.push('make lowercase');
        keystroke = lower;  // look up rules for lowercase symbol
      }
    }

    // tack on all rules that may run
    const rules = getJustRules(this.ruleSet?.rules);
    if (rules !== undefined) {
      for (const rule of rules) {
        if (rule.from === keystroke) {
          const str = ruleToString(rule);
          if (str) {
            help.push(str);
          }
        }
      }
    }

    return help;
  }

  /** on success, save rules & return true - else return list of errors */
  private compileRules(ruleSet: RuleSet): true | string[] {
    const [compiled, failed] = compileRuleSet(ruleSet);
    if (failed.length > 0) {
      return failed.map(f => failedRuleToString(f));
    }
    // only save if successful
    this.version++;
    this.ruleSet = ruleSet;
    this.compiledRules = compiled;
    return true;
  }

  /** update preprocess flags & recompile */
  private updatePreprocess() {
    const preprocess: PreprocessOptions[] = [];
    if (this.preLowerCase) preprocess.push('lowerCase');
    if (this.prePunctuation) preprocess.push('cleanPunctuation');
    this.ruleSet = { ...this.ruleSet, preprocess };
  }

  /** on success, return a list of rules - else return list of errors */
  private parseRules(s: string): RuleType[] | string[] {
    const lines = s.split('\n').map(l => l.trim());

    const rules: RuleType[] = [];
    const errors: string[] = [];
    lines.forEach((s, i) => {
      const result = stringToRule(s);
      if (isRuleParsingError(result)) {
        errors.push(`error line ${i + 1}: "${s}" - ${ruleParsingErrorToString(result)}`);
      } else {
        rules.push(result);
      }
    });

    return errors.length > 0 ? errors : rules;
  }

  /** on success, return a list of rules - else return list of errors */
  private parseMultiRules(s: string): Rules2Type | string[] {
    if (!s.includes('\nreset\n')) {
      // no reset, so it's just a single list of rules
      return this.parseRules(s);
    }

    const lines = s.split('\n').map(l => l.trim());

    const rules: RuleType[][] = [];
    const errors: string[] = [];
    let list: RuleType[] = [];
    lines.forEach((s, i) => {
      if (s === 'reset') {
        // peel off current list & start a new one
        if (list.length > 0) {
          rules.push(list);
          list = [];
        }
      } else {
        // add rule to current list
        const result = stringToRule(s);
        if (isRuleParsingError(result)) {
          errors.push(`error line ${i + 1}: "${s}" - ${ruleParsingErrorToString(result)}`);
        } else {
          list.push(result);
        }
      }
    });

    if (list.length > 0) {
      rules.push(list);
    }
    return errors.length > 0 ? errors : rules;
  }
}
